import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { TTRestaurantProfile } from '../interfaces/restaurant';
import { RootState } from '../rootReducer';
import store from '../store';

type Props = {
  onCancel: () => void;
};

export const WalkthroughPopUp: React.FC<Props> = ({ onCancel }) => {
  const [open, setOpen] = useState(true);
  const profile = useSelector<RootState, TTRestaurantProfile | null>(
    state => state.restaurant.profile,
  );
  return (
    <Transition.Root show={open} as={Fragment} appear>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {
          setOpen(false);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => {
            setOpen(false);
            onCancel();
          }}
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-surface px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8  sm:p-6">
                <svg
                  className={'text-gray-500 w-8 h-8 top-0 right-0 m-1 fixed'}
                  onClick={() => {
                    localStorage.setItem('_tt_walkthrough', 'true');
                    setOpen(false);
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h2 className="text-3xl leading-9 font-extrabold text-on-surface mb-10">
                    Ordering using SNAPnTAP
                  </h2>
                  <nav aria-label="Progress mt-8">
                    <ol className="overflow-hidden">
                      <li className="relative pb-10">
                        {/*<div className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full "*/}
                        {/*     aria-hidden="true"/>*/}
                        <span className="relative flex items-start group">
                          <span className="h-9 flex items-center">
                            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-primary rounded-full">
                              <span className="h-2.5 w-2.5 bg-primary rounded-full" />
                            </span>
                          </span>
                          <span className="ml-4 min-w-0 flex flex-col">
                            <span className="text-xs text-on-surface font-semibold tracking-wide uppercase">
                              🍽 Browse the menu
                            </span>
                            <span className="text-sm text-on-surface">
                              Check out the menu by clicking the ones available
                              to you.
                            </span>
                          </span>
                        </span>
                      </li>

                      <li className="relative pb-10">
                        {/*<div className={`-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full 'bg-primary-600'}`}*/}
                        {/*     aria-hidden="true"/>*/}
                        <span
                          className="relative flex items-start group"
                          aria-current="step"
                        >
                          <span
                            className="h-9 flex items-center"
                            aria-hidden="true"
                          >
                            <span
                              className="h-9 flex items-center"
                              aria-hidden="true"
                            >
                              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-primary rounded-full">
                                <span className="h-2.5 w-2.5 bg-primary rounded-full" />
                              </span>
                            </span>
                          </span>
                          <span className="ml-4 min-w-0 flex flex-col">
                            <span className="text-xs font-semibold tracking-wide uppercase text-on-surface">
                              👆 Add menu items to your{' '}
                              {profile?.config?.use_order_lang
                                ? 'order'
                                : 'tab'}
                            </span>
                            <span className="text-sm text-on-surface">
                              Click an item that looks good to you, customize
                              it, and then add it to your{' '}
                              {profile?.config?.use_order_lang
                                ? 'order'
                                : 'tab'}
                              .
                            </span>
                          </span>
                        </span>
                      </li>

                      <li className="relative pb-10">
                        {/*<div className={`-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-primary-600}`}*/}
                        {/*     aria-hidden="true"/>*/}
                        <span className="relative flex items-start group">
                          <span
                            className="h-9 flex items-center"
                            aria-hidden="true"
                          >
                            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-primary rounded-full">
                              <span className="h-2.5 w-2.5 bg-primary rounded-full" />
                            </span>
                          </span>
                          <span className="ml-4 min-w-0 flex flex-col">
                            <span className="text-xs font-semibold tracking-wide uppercase text-on-surface">
                              👀 View{' '}
                              {profile?.config?.use_order_lang
                                ? 'order'
                                : 'tab'}{' '}
                              when done adding
                            </span>
                            <span className="text-sm text-on-surface">
                              When done adding items, click "View{' '}
                              {profile?.config?.use_order_lang
                                ? 'Order'
                                : 'Tab'}
                              " at the bottom of your screen to view your{' '}
                              {profile?.config?.use_order_lang
                                ? 'order'
                                : 'tab'}
                              .
                            </span>
                          </span>
                        </span>
                      </li>

                      <li className="relative pb-10">
                        {/*<div className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"*/}
                        {/*     aria-hidden="true"/>*/}
                        <span className="relative flex items-start group">
                          <span
                            className="h-9 flex items-center"
                            aria-hidden="true"
                          >
                            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-primary rounded-full">
                              <span className="h-2.5 w-2.5 bg-primary rounded-full" />
                            </span>
                          </span>
                          <span className="ml-4 min-w-0 flex flex-col">
                            <span className="text-xs font-semibold tracking-wide uppercase text-on-surface">
                              💳 Add your payment info
                            </span>
                            <span className="text-sm text-on-surface">
                              Securely add a valid card to authorize your
                              payment method.
                            </span>
                          </span>
                        </span>
                      </li>

                      <li className="relative pb-10">
                        <span className="relative flex items-start group">
                          <span
                            className="h-9 flex items-center"
                            aria-hidden="true"
                          >
                            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-primary rounded-full">
                              <span className="h-2.5 w-2.5 bg-primary rounded-full" />
                            </span>
                          </span>
                          <span className="ml-4 min-w-0 flex flex-col">
                            <span className="text-xs font-semibold tracking-wide uppercase text-on-surface">
                              👏 Start your{' '}
                              {profile?.config?.use_order_lang
                                ? 'order'
                                : 'tab'}
                            </span>
                            <span className="text-sm text-on-surface">
                              With payment authorized, click “Start your{' '}
                              {profile?.config?.use_order_lang
                                ? 'Order'
                                : 'Tab'}{' '}
                              to send your order to the kitchen!
                            </span>
                          </span>
                        </span>
                      </li>
                      <li className="relative  pb-10">
                        <span className="relative flex items-start group">
                          <span
                            className="h-9 flex items-center"
                            aria-hidden="true"
                          >
                            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-primary rounded-full">
                              <span className="h-2.5 w-2.5 bg-primary rounded-full" />
                            </span>
                          </span>
                          <span className="ml-4 min-w-0 flex flex-col">
                            <span className="text-xs font-semibold tracking-wide uppercase text-on-surface">
                              🔁 Order more or close{' '}
                              {profile?.config?.use_order_lang
                                ? 'order'
                                : 'tab'}
                            </span>
                            <span className="text-sm text-on-surface">
                              Order more by adding to your{' '}
                              {profile?.config?.use_order_lang
                                ? 'order'
                                : 'tab'}{' '}
                              during your experience. Close the{' '}
                              {profile?.config?.use_order_lang
                                ? 'order'
                                : 'tab'}{' '}
                              when you’re done.
                            </span>
                          </span>
                        </span>
                      </li>
                    </ol>
                  </nav>
                  <div className="mb-6">
                    <span
                      className={'font-extrabold mr-1 text-on-surface text-sm'}
                    >
                      Note on Privacy:
                    </span>
                    <span className={'text-sm text-on-surface'}>
                      We collect your email to send you receipts after your
                      meal, and we collect your phone number to keep your
                      payment information secure. Your email and phone number
                      are not shared with third-parties, sold, or used for any
                      other reason than your protection.
                    </span>
                  </div>
                  <div className="mb-6">
                    <span
                      className={'font-extrabold mr-1 text-on-surface text-sm'}
                    >
                      Need Help?
                    </span>
                    <span className={'text-sm text-on-surface'}>
                      Click the question mark at the top right corner of your
                      screen to message us directly so we can help you.
                    </span>
                  </div>
                  <p className={'text-sm text-on-surface'}>
                    Thank you for supporting restaurants. All SNAPnTAP orders
                    are sent with ♥
                  </p>
                </div>
                <div className="mt-5 sm:mt-6">
                  <button
                    onClick={() => {
                      setOpen(false);
                    }}
                    id={'dismiss-walkthrough-btn'}
                    className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base sm:text-lg font-medium text-on-primary hover:bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
